import type { RoomAvailable } from '@colyseus/sdk'
import { RoomType } from '../../../types/Rooms'

export interface RoomData {
    id: string
    name: string
    description?: string
    password?: string | null
    createdAt: string
    updatedAt: string
    maxUsers?: number
    roomType: RoomType
    backgroundMode?: string
    clients?: number
    maxClients?: number
    roomId: string
    metadata?: any
    creatorName?: string
    hasPassword: boolean
    isPrivate: boolean
    passwordProtected: boolean
    customRoomId?: string
    companyId?: string | null
    /** SC-422: from the room's ColyseusRoom row. */
    mapId?: string
    persistent?: boolean
    isLobby?: boolean
}

// Helper to serialize dates while preserving original values
const serializeDate = (dateValue: any): string => {
    if (dateValue instanceof Date) {
        return dateValue.toISOString()
    }
    if (typeof dateValue === 'string') {
        // Validate it's a valid ISO string, otherwise create new date
        const parsed = new Date(dateValue)
        if (!isNaN(parsed.getTime())) {
            return dateValue
        }
    }
    // Only create new date if no valid date exists
    return new Date().toISOString()
}

export const transformRoomData = (room: RoomAvailable): RoomData => {
    const metadata = room.metadata || {};
    const roomName = metadata.name || room.name || 'Unnamed Room';
    const roomDescription = metadata.description || '';
    const roomType = metadata.roomType || RoomType.CUSTOM;
    const creatorName = metadata.playerName || (room as any).playerName || 'Unknown';

    // Preserve original dates from room data if available
    // RoomAvailable doesn't have createdAt/updatedAt directly, check metadata first
    const createdAt = serializeDate(
        metadata.createdAt || 
        (room as any).createdAt
    )
    const updatedAt = serializeDate(
        metadata.updatedAt || 
        (room as any).updatedAt
    )

    return {
        id: room.roomId,
        roomId: room.roomId,
        roomType: roomType,
        name: roomName,
        description: roomDescription,
        creatorName: creatorName,
        hasPassword: metadata.hasPassword || false,
        isPrivate: metadata.isPrivate || metadata.hasPassword || false,
        passwordProtected: metadata.passwordProtected || metadata.hasPassword || false,
        createdAt: createdAt,
        updatedAt: updatedAt,
        clients: room.clients,
        maxClients: room.maxClients,
        metadata: metadata,
        customRoomId: metadata?.customRoomId,
        companyId: metadata?.companyId ?? undefined,
        mapId: metadata?.mapId,
        persistent: metadata?.persistent === true,
        isLobby: metadata?.isLobby === true,
    };
}; 